/* ==========================================================================
   Serinia – Newsletter-Fenster (sections/sr-newsletter.liquid)
   - erscheint nach X Sekunden sichtbarer Seite, nie über einem anderen Fenster
   - höchstens einmal pro Besuch (30 Min. ohne Aktivität = neuer Besuch),
     nach dem Schliessen 7 Tage Pause, nach einer Anmeldung 365 Tage nicht
   - nicht bei Besuchen aus einer E-Mail (Klaviyo-Link, utm_medium=email)
   - Anmeldung: Klaviyo Client-API -> Liste, Einwilligung E-Mail-Marketing
   - danach Rabattcode im Hintergrund hinterlegen (/discount/…)
   Gespeichert wird nur, wann das Fenster gezeigt/geschlossen/genutzt wurde –
   keine E-Mail-Adresse. Gleicher Schlüssel wie der Popup im alten Theme,
   damit niemand das Angebot nach dem Theme-Wechsel ein zweites Mal sieht.
   Testen: ?sr_popup=test – erscheint trotz Sperren, merkt sich nichts.
   (Die Anmeldung geht auch dann echt an Klaviyo.)
   ========================================================================== */
(() => {
  const S = window.Serinia || {};
  const KEY = 'serinia.popup.v1';
  const DAY = 864e5;
  const VISIT = 30 * 60e3;
  const KLAVIYO = 'https://a.klaviyo.com/client/subscriptions/';

  const get = () => document.getElementById('sr-newsletter-popup');
  const configOf = (dialog) => JSON.parse(dialog.querySelector('[data-sr-newsletter-config]').textContent);
  if (!get()) return;

  const params = new URLSearchParams(location.search);
  const test = params.get('sr_popup') === 'test';

  let memory = {};
  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (_) { return memory; }
  };
  const write = (patch) => {
    if (test) return;
    memory = { ...read(), ...patch };
    try { localStorage.setItem(KEY, JSON.stringify(memory)); } catch (_) { /* nur für diesen Besuch */ }
  };

  const fromEmail = !test && (params.has('_kx')
    || /^(email|klaviyo)$/i.test(params.get('utm_medium') || '')
    || /klaviyo/i.test(params.get('utm_source') || ''));

  const blocked = () => {
    if (test) return false;
    const r = read();
    const now = Date.now();
    if (r.submittedAt && now - r.submittedAt < 365 * DAY) return true;
    if (r.dismissedAt && now - r.dismissedAt < 7 * DAY) return true;
    if (r.shownAt && now - (r.lastActivity || r.shownAt) < VISIT) return true;
    return false;
  };

  // Ein anderes Fenster (Warenkorb, Menü, Suche), Cookie-Banner oder ein
  // Klaviyo-Formular ist offen: warten
  const busyScreen = (dialog) => {
    if ([...document.querySelectorAll('dialog[open]')].some((d) => d !== dialog)) return true;
    return [...document.querySelectorAll('#shopify-pc__banner, .needsclick.klaviyo-form')].some((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
    });
  };

  const show = (dialog, step) => {
    dialog.dataset.state = step;
    dialog.querySelectorAll('[data-step]').forEach((el) => { el.hidden = el.dataset.step !== step; });
  };

  const open = (dialog) => {
    show(dialog, 'form');
    const img = dialog.querySelector('img[loading="lazy"]');
    if (img) img.loading = 'eager';
    S.openDialog?.(dialog);
  };

  /* --- Erscheinen ------------------------------------------------------- */
  const config = configOf(get());

  if (config.designMode) {
    // Theme-Editor: nur zeigen, solange die Section gewählt ist
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail.sectionId === config.sectionId && get()) open(get());
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail.sectionId === config.sectionId) S.closeDialog?.(get());
    });
  } else {
    if (Date.now() - (read().lastActivity || 0) >= VISIT) write({ shownAt: 0 });

    // Aktivität hält den Besuch offen (höchstens alle 30 s schreiben)
    let lastWrite = 0;
    ['pointerdown', 'keydown', 'scroll'].forEach((name) => document.addEventListener(name, () => {
      if (Date.now() - lastWrite < 30e3) return;
      lastWrite = Date.now();
      write({ lastActivity: lastWrite });
    }, { passive: true }));

    if (!fromEmail && !blocked()) {
      let elapsed = 0;
      let last = performance.now();
      const timer = setInterval(() => {
        const now = performance.now();
        const delta = Math.min(now - last, 1500);
        last = now;
        const dialog = get();
        if (!dialog) return clearInterval(timer);
        if (document.hidden || busyScreen(dialog)) return;
        elapsed += delta;
        // Bild kurz vorher laden, damit es beim Öffnen schon da ist
        if (elapsed >= config.delay - 2500) {
          const img = dialog.querySelector('img[loading="lazy"]');
          if (img) img.loading = 'eager';
        }
        if (elapsed < config.delay) return;
        clearInterval(timer);
        if (blocked()) return; // in einem anderen Tab schon gesehen
        open(dialog);
        write({ shownAt: Date.now(), lastActivity: Date.now() });
      }, 500);
    }

    // Schliessen ohne Anmeldung = 7 Tage Pause
    get().addEventListener('close', (event) => {
      if (event.target.dataset.state !== 'done') write({ dismissedAt: Date.now() });
    });
    // Anmeldung in einem anderen Tab: hier nicht mehr anbieten
    window.addEventListener('storage', (event) => {
      if (event.key !== KEY || !read().submittedAt) return;
      const dialog = get();
      if (dialog?.open && dialog.dataset.state !== 'done') S.closeDialog?.(dialog);
    });
  }

  /* --- Anmeldung -------------------------------------------------------- */
  const subscribe = async (cfg, email) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15e3);
    try {
      const response = await fetch(`${KLAVIYO}?company_id=${encodeURIComponent(cfg.companyId)}`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/vnd.api+json',
          accept: 'application/vnd.api+json',
          revision: '2026-07-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Serinia Theme 2026 Newsletter-Fenster',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    properties: {
                      serinia_signup_source: 'theme_2026_popup',
                      serinia_consent_version: 'newsletter-popup-2026-10',
                    },
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: cfg.listId } } },
          },
        }),
      });
      // 202 = angenommen (Klaviyo verarbeitet asynchron)
      if (response.status === 202) return null;
      if (response.status === 429) return 'wait';
      try {
        const body = await response.json();
        if (body.errors?.some((e) => /\/email$/.test(e.source?.pointer || ''))) return 'invalid';
      } catch (_) { /* leere Antwort */ }
      return 'general';
    } catch (error) {
      return error.name === 'AbortError' ? 'general' : 'connection';
    } finally {
      clearTimeout(timeout);
    }
  };

  // Rabatt hinterlegen (Cookie + Warenkorb), danach Warenkorb neu zeichnen
  const applyDiscount = async (code) => {
    if (!code) return;
    try {
      await fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`, { credentials: 'same-origin' });
      if (!S.cart) return;
      const ids = S.cart.sectionIds().join(',');
      const response = await fetch(`${location.pathname}?sections=${ids}`);
      if (response.ok) S.cart.render(await response.json());
    } catch (_) { /* Code steht im Fenster, kann an der Kasse eingegeben werden */ }
  };

  document.addEventListener('input', (event) => {
    const note = event.target.closest?.('[data-sr-newsletter-form]')?.querySelector('[data-sr-newsletter-error]');
    if (note) note.hidden = true;
  });

  // Läuft nach der Prüfung in sr-theme.js (die markiert ungültige Adressen)
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('[data-sr-newsletter-form]');
    if (!form) return;
    event.preventDefault();
    const dialog = form.closest('dialog');
    const cfg = configOf(dialog);
    const field = form.querySelector('input[type="email"]');
    const note = form.querySelector('[data-sr-newsletter-error]');
    const button = form.querySelector('[type="submit"]');
    if (button.getAttribute('aria-busy') === 'true') return;
    if (field.getAttribute('aria-invalid') === 'true' || !field.value.trim()) return;
    note.hidden = true;
    if (form.elements.company_website.value) return; // Bot

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const failure = await subscribe(cfg, field.value.trim());
    button.removeAttribute('aria-busy');
    button.disabled = false;

    if (failure) {
      note.textContent = cfg.errors[failure] || cfg.errors.general;
      note.hidden = false;
      if (failure === 'invalid') field.setAttribute('aria-invalid', 'true');
      console.warn('[Serinia Newsletter] Anmeldung fehlgeschlagen:', failure);
      return;
    }

    if (!cfg.designMode) write({ submittedAt: Date.now() });
    form.reset();
    show(dialog, 'done');
    dialog.querySelector('[data-sr-newsletter-done]')?.focus();
    if (!cfg.designMode) applyDiscount(cfg.code);
  });
})();
